import * as React from 'react';
import {FileObject, FileType, getFilePath} from 'api/library';
import {VideoValue} from 'api/lesson/types';
import CardContext from '../CardContext';
import LessonBuilderContext from 'components/LessonBuilder/LessonBuilderContext';
import FileContainer from './components/FileContainer';
import Upload, {UploadVariant} from 'components/Upload';
import VideoPlayer from "components/VideoPlayer";

export interface VideoTemplateProps {
  value?: VideoValue;
}

/**
 * This component renders the video template.
 * @param {VideoTemplateProps} props
 * @return {React.ReactElement<VideoTemplateProps>}
 * @constructor
 */
export default function VideoTemplate(
    props: VideoTemplateProps,
): React.ReactElement<VideoTemplateProps> {
  const cardContext = React.useContext(CardContext);
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];

  /**
   * Set the value.
   * @param {VideoTemplateProps<value>} value
   */
  function setValue(value: VideoTemplateProps['value']): void {
    if (cardContext.index !== undefined) {
      content[cardContext.index].value = value;

      (lesson && setLesson) && setLesson({
        ...lesson,
        content,
      });
    }
  }

  /**
   * Is the video empty.
   * @return {boolean}
   */
  function isEmpty(): boolean {
    return !Boolean(props.value);
  }

  /**
   * Render preview.
   * @return {JSX.Element | undefined}
   */
  function renderPreview(): JSX.Element | undefined {
    /**
     * This delete event handler.
     */
    function handleDelete(): void {
      setValue(undefined);
    }

    return props.value ? (
      <FileContainer id={props.value} onDelete={handleDelete}>
        {(file) => (
          <VideoPlayer src={getFilePath(file.id)} />
        )}
      </FileContainer>
    ) : undefined;
  }

  /**
   * Render upload.
   * @return {JSX.Element}
   */
  function renderUpload(): JSX.Element {
    /**
     * This upload event handler.
     * @param {FileObject} file
     */
    function handleUpload(file: FileObject): void {
      setValue(file.id);
    }

    /**
     * This select event handler.
     * @param {FileObject[]} attached
     */
    function handleSelect(attached: FileObject[]): void {
      setValue(attached[0].id);
    }

    return (
      <Upload
        type={FileType.Video}
        variant={UploadVariant.Accent}
        onUpload={handleUpload}
        picker={{
          onSelect: handleSelect,
        }}
      />
    );
  }

  return (
    <React.Fragment>
      {!isEmpty() ? (
        renderPreview()
      ) : (
        renderUpload()
      )}
    </React.Fragment>
  );
}
